import type { CSSProperties } from 'react';
import { type MascotName, type SceneSection, type SceneVariant, sceneUrl } from '../illustrations';
import { cx } from './cx';
import { Mascot } from './Mascot';
import { BELOW_TABLET_MEDIA, TABLET_UP_MEDIA } from './media';

export type SceneFadeEdge = 'top' | 'right' | 'bottom' | 'left';
export type SceneMascotPosition = 'right' | 'left' | 'center';

export interface SceneProps {
  section: SceneSection;
  /** hero is the wide 1200x420 scene, mobile the 440x420 crop. */
  variant?: SceneVariant;
  mascot?: MascotName;
  mascotPosition?: SceneMascotPosition;
  /**
   * Edges that fade into the canvas. true (default) fades left and bottom on
   * hero (the heading sits on the left) and bottom on mobile; false fades none.
   */
  fade?: boolean | SceneFadeEdge[];
  /**
   * Who picks the variant. Omitted: the caller mounts one variant (with
   * useIsTabletUp), as the in-app pages do. 'css': for a prerendered page,
   * whose static HTML is captured at one width and must be right at every
   * width before JavaScript runs. The caller renders BOTH variants; the
   * stylesheet shows the hero one from 768 and the mobile one below it
   * (Scene.css), the art is a <picture> that switches source at 768 (so
   * the two Scenes share one download per width: the hidden one resolves to
   * the same file as the shown one), and the mascot of the hidden variant
   * resolves to a blank, not its sprite.
   */
  gate?: 'css';
  /** Overrides the mascot's loading (default: eager on hero, lazy on mobile). */
  mascotLoading?: 'lazy' | 'eager';
  className?: string;
  style?: CSSProperties;
}

const DEFAULT_FADE: Record<SceneVariant, SceneFadeEdge[]> = {
  hero: ['left', 'bottom'],
  mobile: ['bottom'],
};

/**
 * Decorative section backdrop: the section's illustrated scene plus an
 * optional giraffe. Hidden from assistive technology as a whole (one
 * aria-hidden on the root) and inert to the pointer (`.ee-scene` in Scene.css),
 * so it can sit behind a heading without stealing a tap. A hero mascot is
 * above the fold, so it loads eagerly; a mobile one stays lazy (mascotLoading
 * overrides either). Size it
 * from the outside (width, or width and height); without a height it keeps
 * the art's aspect ratio.
 */
export function Scene({
  section,
  variant = 'hero',
  mascot,
  mascotPosition = 'right',
  fade = true,
  gate,
  mascotLoading,
  className,
  style,
}: SceneProps) {
  const edges = fade === true ? DEFAULT_FADE[variant] : fade === false ? [] : fade;
  return (
    <div
      className={cx(
        'ee-scene',
        `ee-scene--${variant}`,
        gate === 'css' && 'ee-scene--gate-css',
        ...edges.map((edge) => `ee-scene--fade-${edge}`),
        className,
      )}
      style={style}
      aria-hidden="true"
      data-section={section}
    >
      <div className="ee-scene__art">
        {gate === 'css' ? (
          <picture className="ee-scene__picture">
            <source media={TABLET_UP_MEDIA} srcSet={sceneUrl(section, 'hero')} />
            <img
              className="ee-scene__img"
              src={sceneUrl(section, 'mobile')}
              alt=""
              decoding="async"
              draggable={false}
            />
          </picture>
        ) : (
          <img
            className="ee-scene__img"
            src={sceneUrl(section, variant)}
            alt=""
            decoding="async"
            draggable={false}
          />
        )}
      </div>
      {mascot ? (
        <Mascot
          name={mascot}
          className={cx('ee-scene__mascot', `ee-scene__mascot--${mascotPosition}`)}
          loading={mascotLoading ?? (variant === 'hero' ? 'eager' : 'lazy')}
          skipMedia={
            gate === 'css' ? (variant === 'hero' ? BELOW_TABLET_MEDIA : TABLET_UP_MEDIA) : undefined
          }
        />
      ) : null}
    </div>
  );
}
